import { Component } from 'react';
import { getAdvisorErrorMessage } from './advisorErrorMessage';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Paragraph from 'src/design/components/bodyText/Paragraph';
import PrimaryButton from 'src/design/components/button/PrimaryButton';
import SelectList from 'src/design/components/selectList/SelectList';
import TextInputWithError from 'src/design/components/textInput/TextInputWithError';
import { BsChevronDown, BsFillPersonFill } from 'react-icons/bs';
import { FaBuilding, FaChild, FaCoins, FaPeopleArrows } from 'react-icons/fa';
import {
  breakpointMedium,
  breakpointSmall,
} from 'src/design/styleguide/common/breakpoints';
import FormLabel from 'src/design/components/formLabel/FormLabel';
import { SubHeading } from 'src/design/styleguide/Headings';
import Text from 'src/design/styleguide/text/Text';
import WidgetPanel from 'src/components/dashboard/common/WidgetPanel';
import { layoutStyles } from 'src/components/dashboard/common/styles';
import { purple50 } from 'src/themes/brickx/colors';
import { themeForAdvisorAccount } from 'src/themes/landingPageThemes';
import AdvisorService from 'scripts/services/AdvisorService';
import { errorAlert, successAlert } from 'scripts/redux/actions/alerts';
import Regex from 'scripts/utilities/regex';
import { ACCOUNTTYPES } from 'src/settings/registration';

const ACCOUNT_TYPES = [
  {
    label: 'Individual',
    value: ACCOUNTTYPES.INDIVIDUAL,
    Icon: BsFillPersonFill,
  },
  { label: 'Minor', value: ACCOUNTTYPES.MINOR, Icon: FaChild },
  { label: 'SMSF', value: ACCOUNTTYPES.SMSF, Icon: FaCoins },
  { label: 'Trust', value: ACCOUNTTYPES.TRUST, Icon: FaPeopleArrows },
  { label: 'Company', value: ACCOUNTTYPES.CORPORATION, Icon: FaBuilding },
];

const Intro = styled(Paragraph).attrs({
  textSize: 'small',
})`
  max-width: 650px;
  margin: 0;
`;

const OnboardingPanel = styled(({ className, ...props }) => (
  <WidgetPanel rootClassName={className} {...props} />
))`
  & > div:first-child {
    padding: 16px;
  }

  & > div:last-child {
    ${layoutStyles}
    padding: 32px 16px;
  }

  ${breakpointSmall(`
    & > div:first-child {
      padding: 24px;
    }

    & > div:last-child {
      padding: 32px 24px;
    }
  `)}

  && input[class*='textInput__']:focus {
    border-color: ${themeForAdvisorAccount.colors.primary};
    box-shadow: 0 0 0 2px
      ${themeForAdvisorAccount.colors.lightBackgroundPrimary};
  }
`;

const ContentLayout = styled.div`
  ${layoutStyles}
  align-items: start;

  ${breakpointMedium(`
    grid-template-columns: minmax(0, 1.6fr) minmax(220px, 1fr);
  `)}
`;

const SectionHeading = styled(SubHeading).attrs({
  as: 'h2',
  fontSize: '2x-small',
  align: 'left',
})`
  margin: 0 0 32px;
`;

const ClientDetailsHeading = styled(SectionHeading)`
  margin-bottom: 32px;
`;

const FieldGroup = styled.div`
  margin-bottom: 24px;
`;

const FormInput = styled(TextInputWithError)`
  height: 50px;
  width: 100%;

  &.textInput:focus {
    border-color: ${themeForAdvisorAccount.colors.primary};
    box-shadow: 0 0 0 2px
      ${themeForAdvisorAccount.colors.lightBackgroundPrimary};
  }
`;

const AccountTypeField = styled.div`
  position: relative;

  & > svg:first-child {
    color: ${themeForAdvisorAccount.colors.primary};
    left: 16px;
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
  }
`;

const AccountTypeTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 50px;
  border: 1px solid var(--bx-color---grey-400);
  border-radius: var(--bx-radius);
  padding: 0 16px 0 40px;
  background: transparent;
  color: var(--bx-color---grey-800);
  font-family: inherit;
  font-size: 16px;
  text-align: left;

  &:focus {
    border-color: ${themeForAdvisorAccount.colors.primary};
    box-shadow: 0 0 0 2px
      ${themeForAdvisorAccount.colors.lightBackgroundPrimary};
    outline: 0;
  }

  & > svg {
    color: var(--bx-color---grey-500);
    flex: 0 0 auto;
    font-size: 16px;
  }
`;

const AccountTypeMenu = styled.div`
  ul {
    margin-top: 4px;
    height: auto;
    max-height: none;
    overflow: visible;
    border-color: var(--bx-color---grey-300);
    border-radius: var(--bx-radius);
    box-shadow: var(--bx-shadow);
  }

  ul:focus {
    border-color: ${themeForAdvisorAccount.colors.primary};
    outline: 0;
  }

  li {
    padding: 12px 16px;
  }

  li:hover,
  li:focus {
    background: ${themeForAdvisorAccount.colors.lightBackgroundPrimary};
    color: ${themeForAdvisorAccount.colors.primary};
  }
`;

const SubmitRow = styled.div`
  margin-top: 0;
`;

const AdvisorButton = styled(PrimaryButton)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-width: 120px;
    height: 45px;
    padding: 0 16px;
    border-radius: var(--bx-radius);
    white-space: nowrap;
  }

  &&:not([disabled]) {
    background-color: ${themeForAdvisorAccount.colors.primary};
  }

  &&[disabled],
  &&[disabled]:hover {
    background-color: var(--bx-color---grey-300);
    color: var(--bx-color---grey-500);
    transform: none;
  }

  ${breakpointSmall(`
    && {
      width: auto;
    }
  `)}
`;

const FormHint = styled(Text).attrs({
  as: 'p',
  fontSize: '4x-small',
})`
  margin: 12px 0 0;
  color: var(--bx-color---grey-500);
`;

const NextSteps = styled.section`
  border-top: 1px solid var(--bx-color---grey-200);
  padding-top: 32px;

  ${breakpointMedium(`
    border-left: 1px solid var(--bx-color---grey-200);
    border-top: 0;
    padding-left: 32px;
    padding-top: 0;
  `)}
`;

const StepsList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Step = styled.li`
  display: flex;
  align-items: flex-start;
  min-height: 50px;

  & + & {
    margin-top: 12px;
  }
`;

const StepNumber = styled.span`
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  margin-right: 12px;
  border-radius: 50%;
  background: ${purple50};
  color: ${themeForAdvisorAccount.colors.primary};
  font-size: 12px;
  font-weight: bold;
  line-height: 24px;
  text-align: center;
`;

const StepText = styled(Text).attrs({
  as: 'span',
  fontSize: '4x-small',
})`
  padding-top: 0;
  color: var(--bx-color---grey-800);
`;

const ManualNote = styled(Text).attrs({
  as: 'p',
  fontSize: '4x-small',
})`
  display: flex;
  align-items: flex-start;
  border-top: 1px solid var(--bx-color---grey-200);
  margin: 24px 0 0;
  padding-top: 32px;
  color: var(--bx-color---grey-800);
`;

const ManualNoteIcon = styled.span`
  display: inline-flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 12px;
  border-radius: 50%;
  background: ${purple50};
  color: ${themeForAdvisorAccount.colors.primary};
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
`;

const ManualNoteCopy = styled.span`
  padding-top: 2px;
`;

@connect(null, {
  errorAlert,
  successAlert,
})
export default class AdvisorClientOnboarding extends Component {
  static propTypes = {
    errorAlert: PropTypes.func.isRequired,
    successAlert: PropTypes.func.isRequired,
  };

  state = {
    givenName: '',
    clientEmail: '',
    accountType: ACCOUNTTYPES.INDIVIDUAL,
    accountTypeMenuOpen: false,
    touchedGivenName: false,
    touchedClientEmail: false,
    submitting: false,
  };

  unmounted = false;

  componentWillUnmount() {
    this.unmounted = true;
  }

  isEmailValid = (email) => !!email && !!email.match(Regex.email);

  toggleAccountTypeMenu = () => {
    this.setState(
      (state) => ({ accountTypeMenuOpen: !state.accountTypeMenuOpen }),
      () => {
        if (this.state.accountTypeMenuOpen && this.accountTypeMenu) {
          this.accountTypeMenu.listContainerElement.focus();
        }
      },
    );
  };

  selectAccountType = (index) => {
    this.setState({
      accountType: ACCOUNT_TYPES[index].value,
      accountTypeMenuOpen: false,
    });
  };

  onSubmit = async (event) => {
    event.preventDefault();
    if (this.state.submitting) return;
    const givenName = this.state.givenName.trim();
    const clientEmail = this.state.clientEmail.trim();
    const accountType = this.state.accountType;
    const validEmail = this.isEmailValid(clientEmail);

    this.setState({
      touchedGivenName: true,
      touchedClientEmail: true,
    });

    if (!givenName || !validEmail) {
      return;
    }

    this.setState({ submitting: true });

    try {
      await AdvisorService.signupClient({
        givenName,
        email: clientEmail,
        accountType,
      });
      if (this.unmounted) return;

      this.props.successAlert(
        'Client has been registered successfully and onboarding details were sent by email.',
      );
      this.setState({
        givenName: '',
        clientEmail: '',
        accountType: ACCOUNTTYPES.INDIVIDUAL,
        touchedGivenName: false,
        touchedClientEmail: false,
        submitting: false,
      });
    } catch (error) {
      if (this.unmounted) return;

      this.props.errorAlert(
        getAdvisorErrorMessage(
          error,
          'Unable to register this client. Please try again.',
        ),
      );
      this.setState({ submitting: false });
    }
  };

  render() {
    const {
      givenName,
      clientEmail,
      accountType,
      accountTypeMenuOpen,
      touchedGivenName,
      touchedClientEmail,
      submitting,
    } = this.state;
    const trimmedGivenName = givenName.trim();
    const trimmedClientEmail = clientEmail.trim();
    const givenNameInvalid = touchedGivenName && !trimmedGivenName;
    const emailInvalid =
      touchedClientEmail && !this.isEmailValid(trimmedClientEmail);
    const selectedAccountType = ACCOUNT_TYPES.find(
      (type) => type.value === accountType,
    );
    const AccountTypeIcon = selectedAccountType.Icon;

    return (
      <OnboardingPanel title="Register Client">
        <Intro>
          Create a BrickX account for your client. We&apos;ll email them
          instructions to complete onboarding and automatically link their
          account to your adviser profile.
        </Intro>
        <ContentLayout>
          <section aria-labelledby="client-details-heading">
            <ClientDetailsHeading id="client-details-heading">
              Client Details
            </ClientDetailsHeading>

            <form onSubmit={this.onSubmit}>
              <FieldGroup>
                <FormLabel forId="advisor-client-given-name">
                  First name
                </FormLabel>

                <FormInput
                  forId="advisor-client-given-name"
                  value={givenName}
                  onChange={(value) => this.setState({ givenName: value })}
                  onBlur={() => this.setState({ touchedGivenName: true })}
                  placeholder="John"
                  error={givenNameInvalid && 'Please enter a first name.'}
                />
              </FieldGroup>

              <FieldGroup>
                <FormLabel forId="advisor-client-email">
                  Email address
                </FormLabel>

                <FormInput
                  forId="advisor-client-email"
                  type="email"
                  value={clientEmail}
                  onChange={(value) => this.setState({ clientEmail: value })}
                  onBlur={() => this.setState({ touchedClientEmail: true })}
                  placeholder="client@example.com"
                  error={emailInvalid && 'Please enter a valid email address.'}
                />
              </FieldGroup>

              <FieldGroup>
                <FormLabel forId="advisor-client-account-type">
                  Account type
                </FormLabel>

                <AccountTypeField
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      this.setState({ accountTypeMenuOpen: false });
                    }
                  }}
                >
                  <AccountTypeIcon aria-hidden="true" />
                  <AccountTypeTrigger
                    id="advisor-client-account-type"
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={accountTypeMenuOpen}
                    onClick={this.toggleAccountTypeMenu}
                  >
                    {selectedAccountType.label}
                    <BsChevronDown aria-hidden="true" />
                  </AccountTypeTrigger>
                  {accountTypeMenuOpen && (
                    <AccountTypeMenu>
                      <SelectList
                        ref={(component) => {
                          this.accountTypeMenu = component;
                        }}
                        items={ACCOUNT_TYPES.map((type) => type.label)}
                        onItemSelect={this.selectAccountType}
                        role="listbox"
                      />
                    </AccountTypeMenu>
                  )}
                </AccountTypeField>
              </FieldGroup>

              <SubmitRow>
                <AdvisorButton
                  type="submit"
                  color={PrimaryButton.colors.PURPLE}
                  size={PrimaryButton.sizes.EXTRA_SMALL}
                  disabled={
                    !trimmedGivenName ||
                    !this.isEmailValid(trimmedClientEmail) ||
                    submitting
                  }
                >
                  {submitting ? 'Registering...' : 'Register Client'}
                </AdvisorButton>
                <FormHint>
                  An onboarding email will be sent to the client.
                </FormHint>
              </SubmitRow>
            </form>
          </section>

          <NextSteps aria-labelledby="next-steps-heading">
            <SectionHeading id="next-steps-heading">
              What Happens Next?
            </SectionHeading>
            <StepsList>
              <Step>
                <StepNumber>1</StepNumber>
                <StepText>
                  Their BrickX account is created and linked to you
                </StepText>
              </Step>
              <Step>
                <StepNumber>2</StepNumber>
                <StepText>We send them an account activation email</StepText>
              </Step>
              <Step>
                <StepNumber>3</StepNumber>
                <StepText>
                  They activate their account using the link in the email
                </StepText>
              </Step>
              <Step>
                <StepNumber>4</StepNumber>
                <StepText>
                  They complete the remaining onboarding steps
                </StepText>
              </Step>
            </StepsList>
            <ManualNote>
              <ManualNoteIcon aria-hidden="true">i</ManualNoteIcon>
              <ManualNoteCopy>
                You can track your client&apos;s onboarding progress from the
                Clients page.
              </ManualNoteCopy>
            </ManualNote>
          </NextSteps>
        </ContentLayout>
      </OnboardingPanel>
    );
  }
}
